import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme, TextField } from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { Download01, Plus, Trash01 } from '../../../../assets';
import { ItemDocument } from '../../../../app/services/api/orderManagementClient';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { formatDate } from '../utils/ProductDocumentUtils';
import { UserContext } from '../../../../components/shared/useUser';
import { Error as ErrorIcon } from '@mui/icons-material';

const Documents = (props: { productId: number, productDocuments: ItemDocument[], download: Function, upload: Function, delete: Function }) => {
  const { t } = useTranslation('productDetails');
  const theme = useTheme();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | undefined>(undefined);
  const { hasPermissionTo } = useContext(UserContext);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [currentDocumentId, setCurrentDocumentId] = useState<number | null>(null);
  const [documentToUpdateId, setDocumentToUpdateId] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [effectiveStartDate, setEffectiveStartDate] = useState<string | null>(null);
  const [effectiveEndDate, setEffectiveEndDate] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [newDocumentName, setNewDocumentName] = useState<string>('');

  const onOpenDeleteModal = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, documentId: number) => {
    setDeleteModalOpen(true);
    setSelectedDocumentId(documentId);
  };

  const onCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedDocumentId(undefined);
  };

  const handleDelete = () => {
    props.delete(selectedDocumentId);
    onCloseDeleteModal();
  };

  const openCreateModal = () => {
    setIsCreateMode(true);
    setCurrentDocumentId(null);
    setDocumentToUpdateId(null);
    setUploadModalOpen(true);
    setSelectedFile(null);
    setEffectiveStartDate(null);
    setEffectiveEndDate(null);
    setNewDocumentName('');
  };

  function parseDateString(dateStr?: string | null): string | null {
    if (!dateStr) return null;

    const [datePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('/');

    if (!day || !month || !year) return null;

    // Convert to ISO format for input[type="date"] compatibility: YYYY-MM-DD
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }


  const openUploadModal = (rowId: number, documentId: number | undefined) => {
    const doc = props.productDocuments.find(d => d.DocumentId === documentId);
    setIsCreateMode(false);
    setCurrentDocumentId(rowId);
    setDocumentToUpdateId(documentId ?? null);
    setUploadModalOpen(true);
    setSelectedFile(null);
    setEffectiveStartDate(parseDateString(doc?.EffectiveStartDate));
    setEffectiveEndDate(parseDateString(doc?.EffectiveEndDate));
    setNewDocumentName(doc?.DocumentType === 'Other' ? doc.DocumentName ?? '' : '');
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setCurrentDocumentId(null);
    setDocumentToUpdateId(null);
    setSelectedFile(null);
    setEffectiveStartDate(null);
    setEffectiveEndDate(null);
    setIsCreateMode(false);
    setNewDocumentName('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUploadSubmit = async () => {
    setIsUploading(true);
    try {
      if (isCreateMode) {
        if (selectedFile && newDocumentName && effectiveStartDate && effectiveEndDate) {
          await props.upload(
            null,
            null,
            props.productId,
            selectedFile,
            effectiveStartDate,
            effectiveEndDate,
            newDocumentName,
          );
          closeUploadModal();
        }
      } else {
        const doc = props.productDocuments.find(d => d.Id === currentDocumentId);
        await props.upload(
          currentDocumentId,
          documentToUpdateId,
          props.productId,
          selectedFile,
          effectiveStartDate,
          effectiveEndDate,
          doc?.DocumentType === 'Other' ? newDocumentName : null,
        );
        closeUploadModal();
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Box sx={{ m: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="primary"
          size="md"
          onClick={openCreateModal}
          disabled={!hasPermissionTo(['ProductDetailDocumentsUpload'])}
          startIcon={<SvgIcon><Plus fill="white" /></SvgIcon>}
        >
          {t('documentsTab.createDocument')}
        </Button>
      </Box>
      <TableContainer sx={{ borderRadius: '12px' }}
        component='div'>
        <Table size="small">
          <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
            <TableRow>
              <TableCell>
                {t('documentsTab.documentName')}
              </TableCell>
              <TableCell>
                {t('documentsTab.effectiveStartDate')}
              </TableCell>
              <TableCell>
                {t('documentsTab.effectiveEndDate')}
              </TableCell>
              <TableCell>
                {t('documentsTab.LastUpdated')}
              </TableCell>
              <TableCell>
                {t('documentsTab.type')}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {props.productDocuments.map((row, index) => (
              <TableRow key={`${index}${row.Id}`}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {(row.DocumentId === null || row.DocumentId === undefined) && (
                      <Tooltip title={t('documentsTab.documentNotUploaded')}
                        arrow>
                        <ErrorIcon 
                          fontSize="small" 
                          color="error" 
                          sx={{ mr: 2, width: 16, height: 16, pb: 1 }} 
                        />
                      </Tooltip>
                    )}
                    <Typography variant='textXS'>
                      {row.DocumentName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {formatDate(row.EffectiveStartDate, true)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {formatDate(row.EffectiveEndDate, true)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {formatDate(row.LastUpdated)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='textXS'>
                    {row.DocumentType}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={t('documentsTab.uploadDocument')}>
                    <IconButton
                      color="primary"
                      disabled={!hasPermissionTo(['ProductDetailDocumentsUpload'])}
                      onClick={() => openUploadModal(row.Id, row.DocumentId)}
                    >
                      <UploadFile />
                    </IconButton>
                  </Tooltip>

                  <IconButton
                    disabled={!hasPermissionTo(['ProductDetailDocumentsDownload']) || row.DocumentId === null || row.DocumentId === undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      props.download(row.DocumentId);
                    }}>
                    <SvgIcon fontSize='small'><Download01 /></SvgIcon>
                  </IconButton>
                  <IconButton
                    disabled={!hasPermissionTo(['ProductDetailDocumentsDelete']) || row.DocumentId === null || row.DocumentId === undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDeleteModal(e, row.DocumentId ?? 0);
                    }}>
                    <SvgIcon fontSize='small'><Trash01 color={row.DocumentId ? theme.palette.error[500] : undefined} /></SvgIcon>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmationDialog
        open={deleteModalOpen}
        onCancel={() => onCloseDeleteModal()}
        onConfirm={() => handleDelete()}
        icon={<Trash01 />}
        title={t('documentsTab.confirm')}
        message={t('documentsTab.confirmMessage')}
        confirmButtonText={t('documentsTab.yes')}
        cancelButtonText={t('documentsTab.no')} />

      <Dialog
        open={uploadModalOpen}
        onClose={closeUploadModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('documentsTab.uploadDocument')}
        </DialogTitle>
        <DialogContent>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '16px' }}>
            {(isCreateMode || currentDocumentId == 6) && (
              <TextField
                label={t('documentsTab.documentName')}
                value={newDocumentName}
                onChange={e => setNewDocumentName(e.target.value)}
                fullWidth
                required
                inputProps={{ maxLength: 100 }}
                sx={{ mb: 1 }}
              />
            )}
            <TextField
              label={t('documentsTab.effectiveStartDate')}
              value={effectiveStartDate}
              type="date"
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setEffectiveStartDate(e.target.value)}
              inputProps={{
                max: effectiveEndDate || undefined,
              }}
            />
            <TextField
              label={t('documentsTab.effectiveEndDate')}
              value={effectiveEndDate}
              onChange={(e) => setEffectiveEndDate(e.target.value)}
              type="date"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: effectiveStartDate || undefined,
              }}
            />
            <Button
              variant="secondary"
              component="label"
              startIcon={<UploadFile />}
              fullWidth
            >
              {selectedFile ? selectedFile.name : t('documentsTab.selectFile')}
              <input
                type="file"
                hidden
                onChange={handleFileChange}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadModal}>{t('common:cancel')}</Button>
          <Button
            onClick={handleUploadSubmit}
            variant="primary"
            disabled={
              isUploading ||
              (isCreateMode && !selectedFile) ||
              !effectiveStartDate ||
              !effectiveEndDate ||
              ((isCreateMode || newDocumentName !== '') && !newDocumentName) ||
              (
                !isCreateMode &&
                documentToUpdateId == null &&
                !selectedFile
              )
            }
          >
            {isUploading && <CircularProgress size={16} />}
            {isCreateMode || selectedFile ? t('common:upload') : t('documentsTab.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Documents;